import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { FieldDefinition } from '@/types/cgt'

/** An admin-defined grouping over reusable library fields (many-to-many). */
export interface FieldCategory {
  id: string
  name: string
  description?: string | null
  createdBy?: string | null
  createdAt: string
  updatedAt: string
  fieldCount: number
}

/** A single category plus its ordered member library fields (the copy-by-value payload). */
export interface FieldCategoryDetail extends FieldCategory {
  members: FieldDefinition[]
}

/** Props accepted when creating a category. */
export interface CreateFieldCategoryPayload {
  name: string
  description?: string
  memberFieldIds?: string[]
}

/** Partial props for updating a category (PUT, not PATCH). */
export type UpdateFieldCategoryPayload = Partial<CreateFieldCategoryPayload>

/** Query params accepted by `GET /field-categories`. */
export interface FieldCategoryListParams {
  q?: string
  ids?: string[]
  skip?: number
  take?: number
}

/** Response of `GET /field-categories`. */
export interface FieldCategoryListResult {
  items: FieldCategory[]
  total: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const fieldCategoriesApi = {
  list: (params: FieldCategoryListParams = {}) => {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.ids && params.ids.length > 0) qs.set('ids', params.ids.join(','))
    if (params.skip != null) qs.set('skip', String(params.skip))
    if (params.take != null) qs.set('take', String(params.take))
    const query = qs.toString()
    return request<FieldCategoryListResult>(`/field-categories${query ? `?${query}` : ''}`)
  },

  get: (id: string) => request<FieldCategoryDetail>(`/field-categories/${id}`),

  create: (payload: CreateFieldCategoryPayload) =>
    request<FieldCategory>('/field-categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: UpdateFieldCategoryPayload) =>
    request<FieldCategory>(`/field-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  remove: (id: string) =>
    request<{ ok: true }>(`/field-categories/${id}`, { method: 'DELETE' }),
}
